/**
 * Client-Side Auth Context for CSR Architecture
 * Uses browser-based authentication instead of API calls
 */

import React, { createContext, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useAuth as useAuthStore } from '../stores/authStore';
import { useSentryUser } from '@/hooks/useSentryUser';
import type { AuthUser } from '../stores/authStore';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Email/password authentication (CSR mode)
  login: (credentials: { email: string; password: string }) => Promise<boolean>;
  register: (data: { email: string; password: string; name?: string }) => Promise<boolean>;

  logout: () => Promise<void>;
  updateProfile: (updates: Partial<Pick<AuthUser, 'name' | 'avatar'>>) => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  refreshUser: () => Promise<void>;
  clearError: () => void;

  // Redirect URL management
  setIntendedUrl: (url: string) => void;
  getIntendedUrl: () => string | null;
  clearIntendedUrl: () => void;

  // Legacy OAuth properties for backward compatibility (CSR mode - no-ops)
  loginWithEmail?: (credentials: { email: string; password: string }) => Promise<boolean>;
  authProviders?: string[];
  hasOAuth?: boolean;
  requiresEmailAuth?: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const authStore = useAuthStore();
  const navigate = useNavigate();

  // Sync user context with Sentry for error tracking
  useSentryUser(authStore.user);

  // Redirect URL management
  const INTENDED_URL_KEY = 'auth_intended_url';

  const setIntendedUrl = useCallback((url: string) => {
    try {
      sessionStorage.setItem(INTENDED_URL_KEY, url);
    } catch (error) {
      console.warn('Failed to store intended URL:', error);
    }
  }, []);

  const getIntendedUrl = useCallback((): string | null => {
    try {
      return sessionStorage.getItem(INTENDED_URL_KEY);
    } catch (error) {
      console.warn('Failed to retrieve intended URL:', error);
      return null;
    }
  }, []);

  const clearIntendedUrl = useCallback(() => {
    try {
      sessionStorage.removeItem(INTENDED_URL_KEY);
    } catch (error) {
      console.warn('Failed to clear intended URL:', error);
    }
  }, []);

  // Email/password login using auth store
  const login = useCallback(async (credentials: { email: string; password: string }) => {
    const success = await authStore.login(credentials);

    if (success) {
      // Navigate to intended URL or default to home
      const intendedUrl = getIntendedUrl();
      clearIntendedUrl();
      navigate(intendedUrl || '/');
    }

    return success;
  }, [authStore, navigate, getIntendedUrl, clearIntendedUrl]);

  // Register new user using auth store
  const register = useCallback(async (data: { email: string; password: string; name?: string }) => {
    const success = await authStore.register(data);

    if (success) {
      // Navigate to intended URL or default to home
      const intendedUrl = getIntendedUrl();
      clearIntendedUrl();
      navigate(intendedUrl || '/');
    }

    return success;
  }, [authStore, navigate, getIntendedUrl, clearIntendedUrl]);

  // Logout using auth store
  const logout = useCallback(async () => {
    await authStore.logout();
    navigate('/');
  }, [authStore, navigate]);

  // Update profile using auth store
  const updateProfile = useCallback(async (updates: Partial<Pick<AuthUser, 'name' | 'avatar'>>) => {
    return await authStore.updateProfile(updates);
  }, [authStore]);

  // Change password using auth store
  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    return await authStore.changePassword(currentPassword, newPassword);
  }, [authStore]);

  // Clear error using auth store
  const clearError = useCallback(() => {
    authStore.clearError();
  }, [authStore]);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    // In CSR mode, user data is already in the store
    // This is a no-op for compatibility
  }, []);

  const value: AuthContextType = {
    user: authStore.user,
    isAuthenticated: authStore.isAuthenticated,
    isLoading: authStore.isLoading,
    error: authStore.error,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    refreshUser,
    clearError,
    setIntendedUrl,
    getIntendedUrl,
    clearIntendedUrl,
    // Legacy OAuth properties for backward compatibility (CSR mode - no-ops)
    loginWithEmail: login, // Alias for login in CSR mode
    authProviders: [], // No OAuth providers in CSR mode
    hasOAuth: false, // No OAuth in CSR mode
    requiresEmailAuth: true, // CSR mode requires email auth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper hook for protected routes
export function useRequireAuth(redirectTo = '/') {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate(redirectTo);
    }
  }, [isAuthenticated, isLoading, navigate, redirectTo]);

  return { isAuthenticated, isLoading };
}